import {
  ApproveReturnArgs,
  getAddressString,
} from '@c/modals/ApproveReturnModal';
import {
  AUTH,
  db,
  FirebaseCallable,
  functions,
  httpsCallable,
} from '@util/firebase';
import { formatCurrency } from '@util/index';
import { logError } from '@util/logError';
import { formatReturnReason } from '@util/maps/returnReasons';
import { ADMIN_UID, hasSupportPermission } from '@util/support';
import {
  arrayRemove,
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  QueryDocumentSnapshot,
  startAfter,
  updateDoc,
  where,
} from 'firebase/firestore';
import { AddressDocument } from 'models/address';
import { ChatMessageInput, chatMessagesSchema } from 'models/chat';
import { OrderDocument } from 'models/order';
import { ProductDocument } from 'models/product';
import {
  ExternalLabelProvider,
  ReturnDocument,
  ReturnState,
} from 'models/returns';
import { getChatDocumentById, sendMessageToChat } from '../messages';
import { Reason } from '../orders';
import { voidLabel } from '../shipengine';

const colRef = collection(db, 'returns');

/**
 * How many returns one page of a support list holds.
 *
 * Kept small on purpose. An `in` filter fans out into one sub-query per state,
 * each carrying this limit, so a read costs `page size x states selected`. The
 * old flat cap of 5000 meant two states asked for roughly ten thousand
 * documents and the tab wedged before the query ever settled.
 */
export const RETURNS_PAGE_SIZE = 50;

/** Where a page stopped, handed back so the next one can pick up after it. */
export type ReturnsCursor = QueryDocumentSnapshot | null;

export type ReturnsPage = {
  returns: ReturnDocument[];
  cursor: ReturnsCursor;
  hasMore: boolean;
};

/**
 * One page of returns in the given states, newest first.
 *
 * The states are required rather than optional: the collection is well past ten
 * thousand documents and grows by roughly forty a day, so a caller that wanted
 * one state's worth used to pay for all of them.
 *
 * The cursor is the previous page's last snapshot rather than a `created`
 * value. Firestore reads the ordering fields straight off the snapshot, which
 * keeps the paging correct across the sub-queries an `in` filter fans out into,
 * and avoids depending on how the implicit `__name__` ordering is indexed.
 */
export const getReturnsPage = async ({
  states,
  pageSize = RETURNS_PAGE_SIZE,
  cursor,
}: {
  states: ReturnState[];
  pageSize?: number;
  cursor?: ReturnsCursor;
}): Promise<ReturnsPage> => {
  if (!states.length) return { returns: [], cursor: null, hasMore: false };
  const snapshot = await getDocs(
    query(
      colRef,
      where('state', 'in', states),
      orderBy('created', 'desc'),
      ...(cursor ? [startAfter(cursor)] : []),
      limit(pageSize)
    )
  );
  const docs = snapshot.docs;
  return {
    returns: docs.map((doc) => doc.data() as ReturnDocument),
    cursor: docs.length ? docs[docs.length - 1] : null,
    // A short page is the last one; a full page may or may not be.
    hasMore: docs.length === pageSize,
  };
};

export const getReturnByOrderIdAdmin = async (orderId: string) => {
  const snapshot = await getDocs(
    query(colRef, where('order_id', '==', orderId))
  );
  if (snapshot.empty) return null;
  return snapshot.docs[0].data() as ReturnDocument;
};

export const getReturnByTrackingNumberAdmin = async (
  trackingNumber: string
) => {
  const snapshot = await getDocs(
    query(colRef, where('tracking_number', '==', trackingNumber))
  );
  if (snapshot.empty) return null;
  return snapshot.docs[0].data() as ReturnDocument;
};

export const getReturnsByOrderId = async (
  orderId: string,
  uid: string,
  field: 'buyer_id' | 'seller_id' = 'buyer_id',
  seller_returns_only?: boolean
) => {
  const snapshot = await getDocs(
    query(
      colRef,
      where('order_id', '==', orderId),
      ...(hasSupportPermission(uid) ? [] : [where(field, '==', uid)]),
      ...(seller_returns_only
        ? [where('is_warehouse_return', '==', false)]
        : [])
    )
  );
  return snapshot.docs.map((doc) => doc.data() as ReturnDocument);
};

export const getReturnsByUser = async (
  uid: string,
  field: 'buyer_id' | 'seller_id',
  state?: number
) => {
  const snapshot = await getDocs(
    query(
      colRef,
      where(field, '==', uid),
      ...(typeof state === 'number' ? [where('state', '==', state)] : []),
      ...(field === 'seller_id'
        ? [where('is_warehouse_return', '==', false)]
        : []),
      orderBy('created', 'desc')
    )
  );
  return snapshot.docs.map((doc) => doc.data() as ReturnDocument);
};

export const getReturnById = async (id: string) => {
  const snapshot = await getDoc(doc(colRef, id));
  return snapshot.data() as ReturnDocument;
};

export const getWarehouseReturns = async (
  state?: number,
  max = RETURNS_PAGE_SIZE * 10
) => {
  const snapshot = await getDocs(
    query(
      colRef,
      ...(typeof state === 'number' ? [where('state', '==', state)] : []),
      where('is_warehouse_return', '==', true),
      orderBy('created', 'desc'),
      limit(max)
    )
  );
  return snapshot.docs.map((doc) => doc.data() as ReturnDocument);
};

/**
 * Every return the buyer asked to swap rather than refund, newest first.
 *
 * Support works these alongside upgraded cancellations, so the ordering is done
 * in the query rather than in the table: the two sources are merged before the
 * table ever sees them.
 */
export const getExchangeReturns = async (max = RETURNS_PAGE_SIZE * 10) => {
  const snapshot = await getDocs(
    query(
      colRef,
      where('resolution', '==', 'exchange'),
      orderBy('created', 'desc'),
      limit(max)
    )
  );
  return snapshot.docs.map((doc) => doc.data() as ReturnDocument);
};

export interface InitiateReturnArgs {
  order: OrderDocument;
  products: ProductDocument[];
  reasons: {
    [product_id: string]: string;
  };
  /** How many of each product are coming back. Absent means the ordered qty. */
  quantities?: {
    [product_id: string]: number;
  };
  /**
   * Buyer's message to the seller, posted to the return chat. On anything but
   * a plain refund it carries the request itself: the item wanted in exchange,
   * or the sum being asked for.
   */
  note?: string;
  /**
   * What the buyer wants out of this return. `partial` keeps the items with
   * them, so nothing ships and no label is bought.
   */
  resolution?: ReturnDocument['resolution'];
  /** What the buyer is asking for while keeping the items. */
  partial_refund_amount?: number;
}
export async function initiateReturnRequest(args: InitiateReturnArgs) {
  return await httpsCallable<
    InitiateReturnArgs,
    { chat_id: string; return_id: string }
  >(
    functions,
    FirebaseCallable.initiateReturn
  )(args);
}

/**
 * Settles an approval where MX Locker buys nothing, because the seller is
 * sending the buyer a label of their own or the buyer is shipping the item back
 * on their own.
 *
 * `v2_approveReturn` books a label as part of approving, so there is no way
 * through it that leaves a return without one. Everything it would have written
 * apart from the label is written here instead.
 */
const approveWithExternalLabel = async ({
  id,
  ship_to,
  group_ids,
  external_label,
}: ApproveReturnArgs & { external_label: ExternalLabelProvider }) => {
  const returnDoc = await getReturnById(id);
  // Every return travelling in the box, so one shipment settles as one.
  const boxIds = [id, ...(group_ids ?? [])];
  const approval_date = Date.now();
  const uid = AUTH.currentUser?.uid;
  // An address taken off a user document can be missing its optional lines
  // altogether, and Firestore refuses a write carrying an undefined.
  const destination = Object.fromEntries(
    Object.entries(ship_to).filter(([, value]) => value !== undefined)
  );

  await Promise.all(
    boxIds.map((boxId) =>
      updateDoc(doc(colRef, boxId), {
        state: ReturnState.AwaitingDropoff,
        ship_to: destination,
        approval_date,
        external_label,
        // Tracking is added to one return and has to reach the rest of the box.
        ...(boxIds.length > 1 ? { shipping_group_id: id } : {}),
      })
    )
  );

  // The refund was worked out when the return was opened, with return shipping
  // already taken out of it. Nothing was bought here, so that deduction now
  // covers a label nobody paid for and only a person can put it right.
  const shippingDeducted = returnDoc?.return_shipping_estimate ?? 0;
  const note =
    external_label === 'seller'
      ? 'Approved without an MX Locker label — the seller is sending the buyer a label of their own.'
      : 'Approved without an MX Locker label — the buyer is shipping the item back with their own label.';
  const shippingNote = shippingDeducted
    ? ` ${formatCurrency(
        shippingDeducted
      )} of return shipping still comes out of this refund; adjust the amount when refunding.`
    : '';

  await Promise.all(
    boxIds.map((boxId) =>
      addReturnNote({ id: boxId, note: `${note}${shippingNote}`, uid })
    )
  );

  await sendExternalLabelApprovedMessage({
    returnDoc,
    ship_to,
    external_label,
  });
};

/**
 * Chat renders `content_html` as markup, so a sentence carrying anything a
 * person typed — an address off a seller's account, say — reaches it as text.
 */
const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/** Tells the buyer the return is on, and that no label is coming from us. */
const sendExternalLabelApprovedMessage = async ({
  returnDoc,
  ship_to,
  external_label,
}: {
  returnDoc: ReturnDocument | undefined;
  ship_to: AddressDocument;
  external_label: ExternalLabelProvider;
}) => {
  if (!returnDoc?.chat_id) return;
  try {
    const chat = await getChatDocumentById(returnDoc.chat_id);
    if (!chat) return;
    const source =
      external_label === 'seller'
        ? 'The seller is sending you a return label directly, so no label has been issued here.'
        : 'You are shipping this one back with your own label, so no label has been issued here.';
    const content = `Your return has been approved. ${source} Ship the item to ${getAddressString(
      ship_to
    )}, then reply here with the tracking number so we can follow it.`;
    const message = {
      content,
      content_html: `<div>${escapeHtml(
        content
      )} <a class='underline' href='/dashboard/returns/${encodeURIComponent(
        returnDoc.id
      )}' target='_blank'>View Return</a></div>`,
      is_auto: true,
      auto_recipient: returnDoc.buyer_id,
      uid: ADMIN_UID,
      created_at: Date.now(),
    } as ChatMessageInput;
    await sendMessageToChat(chatMessagesSchema.parse(message), chat);
  } catch (error) {
    logError(error);
  }
};

export const approveReturn = async (args: ApproveReturnArgs) => {
  if (args.external_label) {
    await approveWithExternalLabel({
      ...args,
      external_label: args.external_label,
    });
    // A null answer is how callers hear that an approval was backed out of, so
    // this path answers in the shape the callable would.
    return { data: null };
  }
  return await httpsCallable(functions, 'v2_approveReturn')(args);
};

// The API throws failed-precondition with { estimated, baseline } when the
// return label quote is >10% above what the buyer originally paid for shipping.
// Admins must explicitly accept the higher rate to proceed.
const isHigherRateError = (
  error: unknown
): error is Error & {
  code: string;
  details: { estimated: number; baseline: number };
} => {
  if (!(error instanceof Error)) return false;
  const code = (error as { code?: unknown }).code;
  const details = (error as { details?: unknown }).details as
    | { estimated?: unknown; baseline?: unknown }
    | undefined;
  return (
    (code === 'failed-precondition' ||
      code === 'functions/failed-precondition') &&
    typeof details?.estimated === 'number' &&
    typeof details?.baseline === 'number'
  );
};

export const approveReturnWithHigherRateConfirm = async (
  args: ApproveReturnArgs
) => {
  try {
    return await approveReturn(args);
  } catch (error) {
    if (!isHigherRateError(error)) throw error;
    const { estimated, baseline } = error.details;
    const confirmed = window.confirm(
      `Return shipping is estimated at $${estimated.toFixed(
        2
      )}, more than 10% above the original $${baseline.toFixed(
        2
      )} the buyer paid. Approve anyway?`
    );
    if (!confirmed) return null;
    return await approveReturn({ ...args, accept_higher_rate: true });
  }
};

type DenyReturnOptions = {
  id: string;
  arrived_as_described?: boolean;
  not_responding?: boolean;
};
export const denyReturn = async ({
  id,
  arrived_as_described,
  not_responding,
}: DenyReturnOptions) => {
  const { data } = await httpsCallable<
    DenyReturnOptions,
    { chat_id: string | null }
  >(
    functions,
    'v2_denyReturn'
  )({ id, arrived_as_described, not_responding });
  return data?.chat_id;
};

/**
 * Tells the buyer their offer to keep the items for money back was turned down.
 *
 * The callable writes the message behind the other deny reasons; this one has
 * no flag on it, so the message is posted here the same way a cancellation
 * posts its own.
 */
export const sendPartialRefundDeclinedMessage = async ({
  returnDoc,
  chatId,
}: {
  returnDoc: ReturnDocument;
  chatId?: string | null;
}) => {
  const chat_id = chatId ?? returnDoc.chat_id;
  if (!chat_id) return;
  try {
    const chat = await getChatDocumentById(chat_id);
    if (!chat) return;
    const ask =
      typeof returnDoc.partial_refund_amount === 'number'
        ? `receive ${formatCurrency(returnDoc.partial_refund_amount)} back`
        : 'receive a partial refund';
    const content = `Your request to keep this item and ${ask} was not accepted, so this return has been closed. Reply here if you would like to go over other options.`;
    const message = {
      content,
      content_html: `<div>${content} <a class='underline' href='/dashboard/purchases/${returnDoc.order_id}' target='_blank'>View Order</a></div>`,
      is_auto: true,
      auto_recipient: returnDoc.buyer_id,
      uid: ADMIN_UID,
      created_at: Date.now(),
    } as ChatMessageInput;
    await sendMessageToChat(chatMessagesSchema.parse(message), chat);
  } catch (error) {
    logError(error);
  }
};

export const initiateRefund = async ({
  order,
  returnDoc,
  amount,
}: {
  order: OrderDocument;
  returnDoc: ReturnDocument;
  amount: number;
}) => {
  await httpsCallable<{
    reason: Reason;
    order: OrderDocument;
    partial_amount?: number;
    product_ids?: string[];
    amount_charged?: number;
    return_id: string;
  }>(
    functions,
    'v2_refundOrder'
  )({
    order,
    return_id: returnDoc.id,
    reason: 'product_return',
    partial_amount: amount,
    product_ids: [returnDoc.product_id],
  });
  return {
    id: returnDoc.id,
    amount,
  };
};

export const completeReturn = async ({ id }: { id: string }) => {
  await httpsCallable(functions, 'v2_completeReturn')({ id });
};

export const reopenReturn = async ({
  returnDoc,
}: {
  returnDoc: ReturnDocument;
}) => {
  await updateDoc(doc(colRef, returnDoc.id), {
    state: ReturnState.ReturnRequested,
  });
};

export const cancelReturn = async ({
  returnDoc,
}: {
  returnDoc: ReturnDocument;
}) => {
  // Update return state to cancelled
  try {
    await updateDoc(doc(colRef, returnDoc.id), {
      state: ReturnState.ReturnCancelled,
    });
    // Void label
    const promises = [];
    if (returnDoc.label_id) promises.push(voidLabel(returnDoc.label_id));
    // Send message to buyer
    if (returnDoc.chat_id) {
      const chat = await getChatDocumentById(returnDoc.chat_id);
      if (chat) {
        const message = {
          content:
            'This return has been cancelled due to not shipping in our required time period.',
          content_html: `<div>This return has been cancelled due to not shipping in our required time period. <a class='underline href='/dashboard/purchases/${returnDoc.order_id}' target='_blank'>View Order</a></div>`,
          is_auto: true,
          auto_recipient: returnDoc.buyer_id,
          uid: ADMIN_UID,
          created_at: Date.now(),
        } as ChatMessageInput;
        const messageParsed = chatMessagesSchema.parse(message);
        promises.push(sendMessageToChat(messageParsed, chat));
      }
    }
    await Promise.all(promises);
  } catch (error) {
    logError(error);
    alert((error as Error).message);
  }
};

export const getEligibleRefundAmount = ({
  order_id,
  selected_item,
  reason,
  qty,
}: {
  order_id: string;
  selected_item: string;
  reason: string;
  /** How many units are coming back. Absent means the whole line item. */
  qty?: number;
}) => {
  return httpsCallable<
    {
      order_id: string;
      selected_item: string;
      reason: string;
      qty?: number;
    },
    {
      fee?: number;
      is_eligible: boolean;
      shipping_cost: number;
      total_refund: number;
    }
  >(
    functions,
    'v2_getEligibleRefund'
  )({ order_id, selected_item, reason, qty });
};

export const addReturnNote = async ({
  id,
  note,
  noteHtml,
  uid,
}: {
  id: string;
  note: string;
  noteHtml?: string;
  uid?: string;
}) => {
  await updateDoc(doc(colRef, id), {
    notes: arrayUnion({
      text: note,
      created: Date.now(),
      ...(noteHtml && { text_html: noteHtml }),
      ...(uid && { uid }),
    }),
  });
};

export const deleteReturnNote = async ({
  id,
  note,
}: {
  id: string;
  note: {
    text: string;
    created: number;
    text_html?: string;
    uid?: string;
  };
}) => {
  await updateDoc(doc(colRef, id), {
    notes: arrayRemove(note),
  });
};

// Escape hatch for when the carrier tracking webhook never moves an in-transit
// return to delivered, which blocks the refund actions gated on that state.
export const markReturnAsDelivered = async ({ id }: { id: string }) => {
  await updateDoc(doc(colRef, id), {
    state: ReturnState.ReturnDelivered,
  });
  await addReturnNote({
    id,
    note: 'Return manually marked as delivered by support',
  });
};

export const changeReturnReason = async ({
  id,
  reason,
  previous_reason,
}: {
  id: string;
  reason: string;
  previous_reason?: string | null;
}) => {
  await updateDoc(doc(colRef, id), {
    refund_reason: reason,
  });
  await addReturnNote({
    id,
    note: `Return reason changed from "${
      previous_reason ?? 'none'
    }" to "${formatReturnReason(reason)}"`,
  });
};

export const updateReturnByKey = async ({
  id,
  key,
  value,
}: {
  id: string;
  key: string;
  value: any;
}) => {
  await updateDoc(doc(colRef, id), {
    [key]: value,
  });
};

/**
 * Turns a keep-the-items request into an ordinary return.
 *
 * A `partial` return has nothing shipping, so there is no label to buy and the
 * approve callable refuses it. Dropping the resolution back to `refund` puts
 * the items back on the road and leaves the request approvable, which is the
 * way out when the sum the buyer asked for is not one the seller agrees to.
 */
export const convertPartialRefundToReturn = async ({
  returnDoc,
  uid,
}: {
  returnDoc: ReturnDocument;
  uid?: string;
}) => {
  await updateDoc(doc(colRef, returnDoc.id), {
    resolution: 'refund',
    partial_refund_amount: null,
  });
  const asked =
    typeof returnDoc.partial_refund_amount === 'number'
      ? ` of ${formatCurrency(returnDoc.partial_refund_amount)}`
      : '';
  await addReturnNote({
    id: returnDoc.id,
    note: `Partial refund${asked} was not accepted. Converted to a standard return, so the items ship back.`,
    uid,
  });
};
